import { Box, IconButton } from '@mui/material';
import {
  ArchiveOutlined as ArchiveIcon,
  Delete as DeleteIcon,
  ReportGmailerrorredOutlined as SpamIcon,
  ReportOffOutlined as UnspamIcon,
  Undo as UndoIcon,
  HideImage as HideImageIcon,
} from '@mui/icons-material';
import type { CellContext } from '@tanstack/react-table';
import { isBooleanValueTrue } from '../../../utils/booleanFieldUtils';
import { isInboundFinanceEmailHistoryId } from '../../../utils/financeEmailHistoryId';

const actionsCellBoxSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
  width: '100%',
} as const;

type ActionsCellProps<T> = {
  context: CellContext<T, unknown>;
  onDelete?: (row: T) => void;
  onRestore?: (row: T) => void;
  onDeleteScreenshot?: (row: T) => void;
  onArchiveFinanceEmail?: (row: T) => void;
  onRestoreFinanceEmail?: (row: T) => void;
  onMarkFinanceEmailSpam?: (row: T) => void;
  onUnmarkFinanceEmailSpam?: (row: T) => void;
  markedForDeletion?: Set<string>;
  inlineEditable?: boolean;
  deletingScreenshotIds?: Set<string>;
};

/**
 * Per-row actions: delete/restore, screenshot delete, and optional finance
 * email mailbox actions (archive / spam) when those commands are on the grid.
 */
const ActionsCellComponent = <T extends Record<string, unknown>>({
  context,
  onDelete,
  onRestore,
  onDeleteScreenshot,
  onArchiveFinanceEmail,
  onRestoreFinanceEmail,
  onMarkFinanceEmailSpam,
  onUnmarkFinanceEmailSpam,
  markedForDeletion,
  inlineEditable: inlineEditableProp = false,
  deletingScreenshotIds,
}: ActionsCellProps<T>) => {
  'use memo';
  const { row } = context;
  const meta = context.table.options.meta;
  const inlineEditable = meta?.inlineEditable ?? inlineEditableProp;
  const rowId = String(row.original.Id ?? '');
  // Some LINQ-backed grids expose display-only rows with Id = 0; they have no entity to delete.
  const isVirtualRow = rowId === '' || rowId === '0';
  const isMarkedForDeletion = markedForDeletion?.has(rowId) ?? false;
  const isSoftDeleted = row.original.DeletedAt != null;
  const orig = row.original as Record<string, unknown>;
  const isSpam = isBooleanValueTrue(orig.IsSpam);
  const isInbound = isInboundFinanceEmailHistoryId(rowId);
  const hasScreenshot =
    (orig.PathToScreenshot != null && String(orig.PathToScreenshot) !== '') ||
    (orig.ScreenshotPath != null && String(orig.ScreenshotPath) !== '');
  const isDeletingScreenshot = deletingScreenshotIds?.has(rowId) ?? false;
  const canDelete = Boolean(onDelete) && !isVirtualRow && !isBooleanValueTrue(orig.IsAiGenerated);

  const showFinanceRestore =
    Boolean(onRestoreFinanceEmail) && (isSoftDeleted || !onArchiveFinanceEmail);
  const showFinanceArchive =
    Boolean(onArchiveFinanceEmail) && !isVirtualRow && !isSoftDeleted && !isSpam;
  const showFinanceSpam =
    Boolean(onMarkFinanceEmailSpam) && !isVirtualRow && isInbound && !isSoftDeleted && !isSpam;
  const showFinanceUnspam =
    Boolean(onUnmarkFinanceEmailSpam) &&
    !isVirtualRow &&
    isInbound &&
    (isSpam || !onMarkFinanceEmailSpam);

  if (isSoftDeleted || showFinanceRestore) {
    const restoreHandler = onRestoreFinanceEmail ?? onRestore;
    return (
      <Box sx={actionsCellBoxSx}>
        {restoreHandler && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              restoreHandler(row.original);
            }}
            title="Restore"
            color="primary"
          >
            <UndoIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <Box sx={actionsCellBoxSx}>
      {onDeleteScreenshot && hasScreenshot && !isVirtualRow && (
        <IconButton
          size="small"
          disabled={isDeletingScreenshot}
          onClick={(e) => {
            e.stopPropagation();
            onDeleteScreenshot(row.original);
          }}
          title="Delete screenshot"
          color="error"
        >
          <HideImageIcon fontSize="small" />
        </IconButton>
      )}
      {showFinanceArchive && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onArchiveFinanceEmail?.(row.original);
          }}
          title="Move to archive"
          color="default"
        >
          <ArchiveIcon fontSize="small" />
        </IconButton>
      )}
      {showFinanceSpam && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onMarkFinanceEmailSpam?.(row.original);
          }}
          title="Move to spam"
          color="warning"
        >
          <SpamIcon fontSize="small" />
        </IconButton>
      )}
      {showFinanceUnspam && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onUnmarkFinanceEmailSpam?.(row.original);
          }}
          title="Remove from spam"
          color="primary"
        >
          <UnspamIcon fontSize="small" />
        </IconButton>
      )}
      {canDelete && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(row.original);
          }}
          title={
            inlineEditable
              ? isMarkedForDeletion
                ? 'Unmark for deletion'
                : 'Mark for deletion'
              : 'Delete'
          }
          color={isMarkedForDeletion ? 'success' : 'error'}
        >
          {inlineEditable && isMarkedForDeletion ? (
            <UndoIcon fontSize="small" />
          ) : (
            <DeleteIcon fontSize="small" />
          )}
        </IconButton>
      )}
    </Box>
  );
};

export const ActionsCell = ActionsCellComponent;
